import { useGlobalModal } from '@/hooks/useGlobalModal';
import GlobalModal from '@components/GlobalModal';
import { UserModalTitle } from '@components/modal/UserModalTitle';
import ModalTabs, { Panel } from '@components/ModalTabs';
import { Access } from '@features/authorization';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useEffect, useState, type FC } from 'react';
import { TabSuspenseWrapper } from './common';
import { DeviceAccessTab } from './deviceAccessTab';
import { UserConfigurationModalTab } from './types';

export const UserConfigurationModal: FC = () => {
  const { globalModalProps, state, onClose, changeTabMiddleware, setIsDirty } = useGlobalModal(
    'userConfigurationModal',
    {
      closeConfirmation: true,
    },
  );

  const { panels, value, setValue } = usePanels({
    userId: 'userId' in state ? state.userId : null,
    initialValue: 'selectedTab' in state ? state.selectedTab ?? null : null,
    onClose,
    setIsDirty,
  });

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'xl' }}
      authorization={{
        access: Access.UpdateUser_DeviceUserAccess,
        onClose,
      }}
    >
      {state.open && (
        <>
          <UserModalTitle userId={state.userId} onClose={onClose} variant="tabs" />
          <ModalTabs
            value={value}
            setValue={setValue}
            changeTabMiddleware={changeTabMiddleware}
            panels={panels}
          />
        </>
      )}
    </GlobalModal>
  );
};

const usePanels = ({
  userId,
  initialValue,
  onClose,
  setIsDirty,
}: {
  userId: number | null;
  initialValue: UserConfigurationModalTab | null;
  onClose: () => void;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}): {
  panels: Array<Panel<UserConfigurationModalTab>>;
  value: UserConfigurationModalTab;
  setValue: React.Dispatch<React.SetStateAction<UserConfigurationModalTab>>;
} => {
  const { _ } = useLingui();
  const [selectedTab, setSelectedTab] = useState(UserConfigurationModalTab.Access);

  useEffect(() => {
    setSelectedTab(initialValue ?? UserConfigurationModalTab.Access);
  }, [initialValue, setSelectedTab]);

  if (!userId)
    return {
      panels: [],
      value: selectedTab,
      setValue: setSelectedTab,
    };

  const props = {
    userId,
    onClose,
    setIsDirty,
  };

  return {
    panels: [
      {
        key: UserConfigurationModalTab.Access,
        label: _(msg`Accesos a dispositivos`),
        component: (
          <TabSuspenseWrapper onClose={onClose}>
            <DeviceAccessTab {...props} />
          </TabSuspenseWrapper>
        ),
        access: Access.UpdateUser_DeviceUserAccess,
      },
    ],
    value: selectedTab,
    setValue: setSelectedTab,
  };
};
